import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ApiError, api, fmtRelative } from '../api';
import { t } from '../i18n';

// Notification rows have been written by every domain writer since day one,
// but until now nothing read them back — delivery was web-push only. If the
// browser was closed, or push was never granted, "Yossi logged 3h overtime"
// simply never reached anyone. This is the in-app half.

interface NotificationRow {
  id: string;
  kind: string;
  title: string;
  body: string;
  readAt: string | null;
  createdAt: string;
}

const POLL_MS = 60_000;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [unread, setUnread] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // The sheet is portalled out of this subtree, so "outside" has to be told
  // about it explicitly — otherwise tapping a notification counts as a click
  // outside the panel and closes the thing you just tapped.
  const sheetRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ items: NotificationRow[]; unread: number }>(
        '/v1/notifications?limit=30',
      );
      setItems(r.items);
      setUnread(r.unread);
      setErr(null);
    } catch (e) {
      // A failing inbox must never break the header it lives in.
      setErr(e instanceof ApiError ? e.message : t('Loading failed'));
    }
  }, []);

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Close on outside click and on Escape — a dropdown that traps you is worse
  // than no dropdown.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const target = e.target as Node;
      const inBell = panelRef.current?.contains(target);
      const inSheet = sheetRef.current?.contains(target);
      if (!inBell && !inSheet) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function openPanel() {
    setOpen((v) => !v);
    if (!open) await load();
  }

  async function markAllRead() {
    try {
      await api('/v1/notifications/read-all', { method: 'POST', body: JSON.stringify({}) });
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t('Failed'));
    }
  }

  async function markRead(id: string) {
    try {
      await api(`/v1/notifications/${id}/read`, { method: 'POST', body: '{}' });
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)),
      );
      setUnread((n) => Math.max(0, n - 1));
    } catch {
      // Non-critical — the row stays unread and the next poll corrects it.
    }
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={openPanel}
        aria-label={t('Notifications')}
        aria-expanded={open}
        className="touch relative inline-flex items-center justify-center rounded-md px-3 text-sm text-foreground/80 hover:text-foreground"
      >
        <BellIcon />
        {unread > 0 && (
          <span className="absolute end-1.5 top-1 min-w-[1.15rem] rounded-full bg-danger px-1 text-[10px] font-semibold leading-[1.15rem] text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Rendered into the body, not here.
          On a phone this is a sheet that comes up from the bottom rather than
          a dropdown hanging off a bell in the corner — a panel anchored to a
          header has nowhere to go on a 390px screen, and the last lines were
          simply unreachable. And it has to leave this subtree to do it: the
          app header is blurred, and a blurred element becomes the frame that
          `fixed` positions against, so the sheet was pinning itself to the
          header and sliding off the top of the screen. */}
      {open &&
        createPortal(
          <>
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: this is an aria-hidden, non-focusable scrim whose only job is a tap-outside dismissal for pointer users; keyboard users already close the sheet with Escape (see the keydown listener above), so a key handler here would be unreachable. */}
            <div
              className="fixed inset-0 z-40 bg-primary/40 sm:bg-transparent"
              aria-hidden="true"
              onClick={() => setOpen(false)}
            />
            <div
              ref={sheetRef}
              className="fixed inset-x-0 bottom-0 z-50 max-h-[80dvh] overflow-y-auto rounded-t-2xl border border-border bg-white shadow-[0_-8px_40px_rgba(14,31,54,0.18)] sm:inset-x-auto sm:bottom-auto sm:end-4 sm:top-16 sm:max-h-[70vh] sm:w-[22rem] sm:rounded-lg sm:shadow-[0_20px_60px_rgba(14,31,54,0.14)]"
              style={{ paddingBottom: 'var(--safe-bottom)' }}
            >
              {/* Sticky, because the sheet scrolls and the way out of a sheet must
              never be the thing that scrolls away. */}
              <div className="sticky top-0 flex items-center justify-between border-b border-border bg-white px-4 py-3">
                <span className="text-sm font-semibold">{t('Notifications')}</span>
                {unread > 0 && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline"
                    onClick={markAllRead}
                  >
                    {t('Mark all read')}
                  </button>
                )}
              </div>

              {err && <div className="px-4 py-3 text-sm text-danger">{err}</div>}

              {items.length === 0 && !err ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {t('Nothing new.')}
                </div>
              ) : (
                <ul>
                  {items.map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => !n.readAt && markRead(n.id)}
                        className={`flex w-full flex-col items-start gap-0.5 border-b border-border px-4 py-3 text-start last:border-b-0 ${
                          n.readAt ? 'opacity-60' : 'bg-muted/40'
                        }`}
                      >
                        {/* The title is written by the server in English — it is a
                        short fixed set, so it is translated here rather than
                        making every writer carry four languages. */}
                        <span className="text-sm font-medium">{t(n.title)}</span>
                        <span className="text-xs text-muted-foreground">{n.body}</span>
                        <span className="text-[11px] text-muted-foreground">
                          {fmtRelative(n.createdAt)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}

function BellIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
